import { ColumnDef } from "@tanstack/react-table";
import { ReactNode } from "react";

/**
 * Enhanced table column configuration that combines display, sorting, and field mapping
 */
export interface TableColumnConfig<
  TData = unknown,
  TColumn extends string = string,
> {
  /** Column identifier (enum value) */
  id: TColumn;
  /** Display label for the column header */
  label: string;
  /** Field path in the data object (supports nested paths like "resource.productName") */
  fieldPath: string;
  /** GraphQL sort field name (if different from fieldPath) */
  sortField?: string;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Default visibility state */
  defaultVisible?: boolean;
  /** Custom render function */
  render?: (value: unknown, row: TData) => ReactNode;
  /** Custom click handler */
  onClick?: (row: TData) => void;
  /** Column type for automatic rendering */
  type?: "text" | "date" | "count" | "tag" | "link" | "id" | "custom";
  /** Additional metadata */
  meta?: Record<string, unknown>;
}

/**
 * Configuration for an entire table with reusable column definitions
 */
export interface TableConfiguration<
  TData = unknown,
  TColumn extends string = string,
> {
  /** Table identifier for settings persistence */
  tableId: string;
  /** Column definitions */
  columns: Record<TColumn, TableColumnConfig<TData, TColumn>>;
  /** Default column order */
  defaultColumnOrder: TColumn[];
  /** Default column visibility settings */
  defaultColumnSettings: Record<TColumn, boolean>;
}

/**
 * Extracted and reusable sort field mapping generator
 */
export class SortFieldMapper {
  /**
   * Generate sort field mapping from table configuration
   */
  static generateSortMapping<TData, TColumn extends string>(
    columns: Record<TColumn, TableColumnConfig<TData, TColumn>>,
  ): Record<string, string> {
    const mapping: Record<string, string> = {};

    (Object.values(columns) as TableColumnConfig<TData, TColumn>[]).forEach(
      (column) => {
        if (column.sortable !== false) {
          // Use explicit sortField if provided, otherwise use fieldPath
          const sortField =
            column.sortField || column.fieldPath.replace(/\./g, "__");
          mapping[column.id] = sortField;
        }
      },
    );

    return mapping;
  }

  /**
   * Generate field path mapping from table configuration
   */
  static generateFieldMapping<TData, TColumn extends string>(
    columns: Record<TColumn, TableColumnConfig<TData, TColumn>>,
  ): Record<TColumn, string> {
    const mapping = {} as Record<TColumn, string>;

    (Object.values(columns) as TableColumnConfig<TData, TColumn>[]).forEach(
      (column) => {
        mapping[column.id as TColumn] = column.fieldPath;
      },
    );

    return mapping;
  }

  /**
   * Get sortable column IDs
   */
  static getSortableColumns<TData, TColumn extends string>(
    columns: Record<TColumn, TableColumnConfig<TData, TColumn>>,
  ): TColumn[] {
    return (Object.values(columns) as TableColumnConfig<TData, TColumn>[])
      .filter((column) => column.sortable !== false)
      .map((column) => column.id);
  }
}

/**
 * Enhanced table system that builds on SmartTableSystem with better configuration support
 */
export class ConfigurableTableSystem {
  /**
   * Generate columns from table configuration
   */
  static generateColumnsFromConfig<
    TData extends Record<string, unknown>,
    TColumn extends string,
  >(
    _data: TData[],
    config: TableConfiguration<TData, TColumn>,
    options: {
      columnOrder?: TColumn[];
      columnVisibility?: Record<string, boolean>;
      customColumns?: ColumnDef<TData, unknown>[];
    } = {},
  ): ColumnDef<TData, unknown>[] {
    const columns: ColumnDef<TData, unknown>[] = [];

    // Use provided order or default
    const orderedColumnKeys = options.columnOrder?.length
      ? options.columnOrder
      : config.defaultColumnOrder;

    // Create columns based on configuration
    orderedColumnKeys.forEach((columnKey) => {
      const columnConfig = config.columns[columnKey];
      if (!columnConfig) return;

      // Check visibility (default to column config, then global visibility settings)
      const isVisible =
        options.columnVisibility?.[columnKey] ??
        options.columnVisibility?.[columnConfig.id] ??
        columnConfig.defaultVisible ??
        true;

      if (!isVisible) return;

      const column: ColumnDef<TData, unknown> = {
        id: columnConfig.id,
        accessorKey: columnConfig.fieldPath,
        header: columnConfig.label,
        enableSorting: columnConfig.sortable ?? true,
        meta: columnConfig.meta,
        cell: (props) => {
          const value = props.getValue();
          const row = props.row.original;

          if (columnConfig.render) {
            return columnConfig.render(value, row);
          }

          // Fallback to type-based rendering or simple display
          return this.renderByType(
            columnConfig.type || "text",
            value,
            row as Record<string, unknown>,
            columnConfig.onClick as
              | ((row: Record<string, unknown>) => void)
              | undefined,
          );
        },
      };

      columns.push(column);
    });

    if (options.customColumns) {
      columns.push(...options.customColumns);
    }

    return columns;
  }

  /**
   * Generate GraphQL sort parameter from table configuration and current sorting state
   */
  static generateSortParameter<TData, TColumn extends string>(
    columns: Record<TColumn, TableColumnConfig<TData, TColumn>>,
    sorting: { id: string; desc: boolean }[],
  ): string | undefined {
    if (sorting.length === 0) return undefined;

    const sortMapping = SortFieldMapper.generateSortMapping(columns);
    const firstSort = sorting[0];
    const sortField = sortMapping[firstSort.id];

    if (!sortField) return undefined;

    return `${firstSort.desc ? "-" : ""}${sortField}`;
  }

  private static renderByType(
    type: string,
    value: unknown,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _row: Record<string, unknown>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _onClick?: (row: Record<string, unknown>) => void,
  ): ReactNode {
    if (value == null) return "-";

    switch (type) {
      case "date":
        return new Date(value as string).toLocaleDateString();
      case "count":
        return String(value);
      case "link":
        // This would need to be replaced with actual Clickable component
        return String(value); // Simplified for now
      case "text":
      default:
        return String(value);
    }
  }
}

/**
 * Utility to create type-safe table configurations
 */
export function createTableConfiguration<TData, TColumn extends string>(
  config: TableConfiguration<TData, TColumn>,
): TableConfiguration<TData, TColumn> {
  return config;
}

/**
 * Hook-like helper to extract common table configuration values
 */
export function useTableConfiguration<TData, TColumn extends string>(
  config: TableConfiguration<TData, TColumn>,
) {
  return {
    sortFieldMapping: SortFieldMapper.generateSortMapping(config.columns),
    fieldMapping: SortFieldMapper.generateFieldMapping(config.columns),
    sortableColumns: SortFieldMapper.getSortableColumns(config.columns),
    columnLabels: Object.fromEntries(
      Object.entries(config.columns).map(([key, col]) => [
        key,
        (col as TableColumnConfig<TData, TColumn>).label,
      ]),
    ),
    defaultColumnSettings: config.defaultColumnSettings,
    defaultColumnOrder: config.defaultColumnOrder,
  };
}
